import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Chip,
  Grid,
  Typography,
  CircularProgress,
  TablePagination,
} from '@material-ui/core';
import {
  FilterList as FilterIcon,
  Warning as WarningIcon,
  Edit as EditIcon,
  Add as AddIcon,
} from '@material-ui/icons';
import ReactSelect from 'react-select';
import CourseYearPropertiesService from '../../_services/CourseYearPropertiesService';
import TimeCraftInputService from '../../_services/TimeCraftInputService';
import ProgramManagementService from '../../_services/ProgramManagementService';
import { getShifts } from '../../_services/classroom-shift.service';
import InstructorAssignmentDialog, { InstructorAssignment } from '../../components/InstructorAssignmentDialog';
import EditCYPDialog, { EditableCYPFields } from '../../components/EditCYPDialog';
import AddCYPDialog, { AddCYPSubmitBody } from '../../components/AddCYPDialog';
import './course-year-properties.css';

interface CYPRecord {
  id: number;
  name: string;
  courseId: number;
  moduleCode: string;
  moduleName: string;
  sessions: number | null;
  startDate: string;
  endDate: string;
  examStartDate: string | null;
  examEndDate: string | null;
  reexamDate: string | null;
  credits: number | null;
  localFee: number | null;
  internationalFee: number | null;
  adjustableAmount: number | null;
  courseDisplayName: string | null;
  openForAddOrDrop: number | null;
  instructors: InstructorAssignment[] | null;
  additionalInfo: any;
  shiftId: number | null;
  shiftName: string | null;
  termId: number | null;
  termName: string | null;
  programId: number | null;
  programName: string | null;
  programShortCode: string | null;
}

// Show the program short code alongside the name in the dropdown,
// e.g. "BACC — Bachelor of Arts (Honours) Accounting and Finance".
const formatProgramLabel = (p: any): string =>
  p.programShortCode ? `${p.programShortCode} — ${p.name}` : p.name;

const CourseYearProperties: React.FC = () => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [programIds, setProgramIds] = useState<number[]>([]);
  const [programs, setPrograms] = useState<any[]>([]);
  const [moduleCode, setModuleCode] = useState('');
  const [records, setRecords] = useState<CYPRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  // Instructor dialog
  const [instructorDialogOpen, setInstructorDialogOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<CYPRecord | null>(null);

  // Edit dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editRecord, setEditRecord] = useState<CYPRecord | null>(null);

  // Add dialog
  const [addDialogOpen, setAddDialogOpen] = useState(false);

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  // Shift lookup (id -> name) for resolving names after edits
  const [shiftLookup, setShiftLookup] = useState<Record<number, string>>({});

  useEffect(() => {
    loadPrograms();
    loadShiftLookup();
    // Auto-run the search on load. Dates are optional — an empty range
    // returns all course year properties.
    loadRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadShiftLookup = async () => {
    try {
      const response: any = await getShifts({ limit: 500 });
      const raw =
        (Array.isArray(response?.data) && response.data) ||
        response?.shifts ||
        response?.data?.shifts ||
        [];
      const map: Record<number, string> = {};
      raw.forEach((s: any) => {
        if (s?.id != null) map[s.id] = s.name;
      });
      setShiftLookup(map);
    } catch {
      setShiftLookup({});
    }
  };

  const loadPrograms = async () => {
    try {
      const response: any = await TimeCraftInputService.listPrograms();
      if (response.data.success) {
        setPrograms(response.data.programs || []);
      }
    } catch (error) {
      console.error('Failed to load programs:', error);
    }
  };

  const loadRecords = async () => {
    setLoading(true);
    try {
      const response: any = await CourseYearPropertiesService.list({
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        programIds: programIds.length > 0 ? programIds : undefined,
        moduleCode: moduleCode || undefined,
        limit: rowsPerPage,
        offset: page * rowsPerPage,
      });
      if (response.data.success) {
        // Parse instructors JSON if needed
        const parsed = (response.data.data || []).map((r: any) => ({
          ...r,
          instructors: parseInstructors(r.instructors),
        }));
        setRecords(parsed);
        setTotalCount(response.data.totalCount || 0);
      }
    } catch (error: any) {
      alert('Failed to load records: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const parseInstructors = (val: any): InstructorAssignment[] => {
    if (!val) return [];
    if (Array.isArray(val)) return val;
    if (typeof val === 'string') {
      try {
        const parsed = JSON.parse(val);
        return Array.isArray(parsed) ? parsed : [];
      } catch { return []; }
    }
    return [];
  };

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedIds(records.map(r => r.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id: number) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  return (
    <div className="cyp-page">
      <Box mb={3}>
        <Typography variant="h5" style={{ fontWeight: 600 }}>
          Course Year Properties
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Manage module schedules, instructor assignments, and course properties
        </Typography>
      </Box>

      {/* Filters */}
      <Paper style={{ padding: 16, marginBottom: 20 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={2}>
            <div>
              <label style={{ display: 'block', marginBottom: 4, fontSize: 13, color: 'var(--text-body)' }}>Start Date</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                style={{ width: '100%', padding: '8px', border: '1px solid var(--border-strong)', borderRadius: 4, fontSize: 14 }}
              />
            </div>
          </Grid>
          <Grid item xs={12} md={2}>
            <div>
              <label style={{ display: 'block', marginBottom: 4, fontSize: 13, color: 'var(--text-body)' }}>End Date</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                style={{ width: '100%', padding: '8px', border: '1px solid var(--border-strong)', borderRadius: 4, fontSize: 14 }}
              />
            </div>
          </Grid>
          <Grid item xs={12} md={3}>
            <div>
              <label style={{ display: 'block', marginBottom: 4, fontSize: 13, color: 'var(--text-body)' }}>Program(s)</label>
              <ReactSelect
                isMulti
                options={programs.map((p: any) => ({ value: p.id, label: formatProgramLabel(p) }))}
                value={programs
                  .filter((p: any) => programIds.includes(p.id))
                  .map((p: any) => ({ value: p.id, label: formatProgramLabel(p) }))}
                onChange={(selected: any) => {
                  setProgramIds(selected ? selected.map((s: any) => s.value) : []);
                }}
                placeholder="All Programs"
                isClearable
                styles={{
                  control: (base: any) => ({ ...base, minHeight: 38, fontSize: 14 }),
                  menu: (base: any) => ({ ...base, zIndex: 9999 }),
                  multiValue: (base: any) => ({ ...base, maxWidth: 180 }),
                }}
              />
            </div>
          </Grid>
          <Grid item xs={12} md={2}>
            <div>
              <label style={{ display: 'block', marginBottom: 4, fontSize: 13, color: 'var(--text-body)' }}>Module Code</label>
              <input
                type="text"
                value={moduleCode}
                onChange={(e) => setModuleCode(e.target.value)}
                placeholder="e.g. CHM, PHA..."
                style={{ width: '100%', padding: '8px', border: '1px solid var(--border-strong)', borderRadius: 4, fontSize: 14 }}
              />
            </div>
          </Grid>
          <Grid item xs={12} md={3}>
            <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<FilterIcon />}
                onClick={loadRecords}
                disabled={loading}
                style={{ flex: 1 }}
              >
                Search
              </Button>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => setAddDialogOpen(true)}
                style={{ flex: 1 }}
              >
                Add
              </Button>
            </div>
          </Grid>
        </Grid>
      </Paper>

      {/* Results count */}
      {records.length > 0 && (
        <Box mb={2} display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="body2" color="textSecondary">
            Showing {records.length} of {totalCount} records
          </Typography>
        </Box>
      )}

      {/* Table */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={selectedIds.length > 0 && selectedIds.length === records.length}
                  indeterminate={selectedIds.length > 0 && selectedIds.length < records.length}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell style={{ width: 48 }}></TableCell>
              <TableCell>Module Code</TableCell>
              <TableCell>Module Name</TableCell>
              <TableCell>Program</TableCell>
              <TableCell>Term</TableCell>
              <TableCell>Start Date</TableCell>
              <TableCell>End Date</TableCell>
              <TableCell>Sessions</TableCell>
              <TableCell>Default Shift</TableCell>
              <TableCell>Instructors</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={11} align="center" style={{ padding: 40 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : records.length === 0 ? (
              <TableRow>
                <TableCell colSpan={11} align="center" style={{ padding: 40 }}>
                  <Typography color="textSecondary">
                    No records found. Select filters and click "Search".
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              records.map((record) => (
                <TableRow key={record.id} hover>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedIds.includes(record.id)}
                      onChange={() => handleSelectOne(record.id)}
                    />
                  </TableCell>
                  <TableCell style={{ width: 48, padding: '0 4px' }}>
                    <Button
                      size="small"
                      style={{ minWidth: 32, padding: 4 }}
                      onClick={() => {
                        setEditRecord(record);
                        setEditDialogOpen(true);
                      }}
                    >
                      <EditIcon fontSize="small" style={{ color: 'var(--text-body)' }} />
                    </Button>
                  </TableCell>
                  <TableCell>
                    <strong>{record.moduleCode}</strong>
                  </TableCell>
                  <TableCell>{record.moduleName || 'N/A'}</TableCell>
                  <TableCell>
                    <Typography variant="body2" style={{ fontSize: 13 }}>
                      {record.programShortCode || record.programName || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" style={{ fontSize: 13 }}>
                      {record.termName || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {record.startDate ? new Date(record.startDate).toLocaleDateString() : 'N/A'}
                  </TableCell>
                  <TableCell>
                    {record.endDate ? new Date(record.endDate).toLocaleDateString() : 'N/A'}
                  </TableCell>
                  <TableCell>{record.sessions || 'N/A'}</TableCell>
                  <TableCell>
                    {record.shiftName ? (
                      <Typography variant="body2" style={{ fontSize: 13 }}>
                        {record.shiftName}
                      </Typography>
                    ) : (
                      <Typography variant="body2" style={{ fontSize: 13, color: 'var(--text-faint)', fontStyle: 'italic' }}>
                        Not set
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box
                      style={{ cursor: 'pointer' }}
                      onClick={() => {
                        setSelectedRecord(record);
                        setInstructorDialogOpen(true);
                      }}
                    >
                      {record.instructors && record.instructors.length > 0 ? (
                        <Box>
                          {record.instructors.map((inst, idx) => (
                            <Typography key={idx} variant="body2" style={{
                              fontSize: 13,
                              fontWeight: inst.role === 'primary' ? 600 : 400,
                              color: inst.role === 'primary' ? '#111' : '#666',
                              lineHeight: 1.6,
                            }}>
                              <span style={{
                                display: 'inline-block',
                                width: 7,
                                height: 7,
                                borderRadius: '50%',
                                backgroundColor: inst.role === 'primary' ? '#2563eb' : '#d1d5db',
                                marginRight: 6,
                                verticalAlign: 'middle',
                              }} />
                              {inst.name}
                            </Typography>
                          ))}
                        </Box>
                      ) : (
                        <Chip
                          label="Not Assigned"
                          size="small"
                          icon={<WarningIcon />}
                          style={{ backgroundColor: '#ff9800', color: 'white', cursor: 'pointer' }}
                        />
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        {totalCount > 0 && (
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={(_, newPage) => { setPage(newPage); loadRecords(); }}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[25, 50, 100]}
          />
        )}
      </TableContainer>

      {/* Edit CYP Dialog */}
      {editRecord && (
        <EditCYPDialog
          open={editDialogOpen}
          record={editRecord}
          onSave={async (id, fields) => {
            await CourseYearPropertiesService.update(id, fields);
            // Update local state with saved values, resolving shiftName from lookup
            setRecords(prev =>
              prev.map(r => {
                if (r.id !== id) return r;
                const next: CYPRecord = { ...r, ...fields } as CYPRecord;
                if ('shiftId' in fields) {
                  next.shiftName = fields.shiftId != null ? shiftLookup[fields.shiftId] || null : null;
                }
                return next;
              })
            );
          }}
          onClose={() => {
            setEditDialogOpen(false);
            setEditRecord(null);
          }}
        />
      )}

      {/* Add CYP Dialog */}
      <AddCYPDialog
        open={addDialogOpen}
        programs={programs.map((p: any) => ({ id: p.id, name: p.name }))}
        onSubmit={async (body: AddCYPSubmitBody) => {
          const r = await ProgramManagementService.addModule(body.programId, {
            courseId: body.courseId,
            yearId: body.yearId,
            termId: body.termId ?? null,
            credits: body.credits ?? null,
            startDate: body.startDate ?? null,
            endDate: body.endDate ?? null,
          });
          if (!r.success) {
            throw new Error(r.message || 'Failed to add module');
          }
          setAddDialogOpen(false);
          loadRecords();
        }}
        onClose={() => setAddDialogOpen(false)}
      />

      {/* Instructor Assignment Dialog */}
      <InstructorAssignmentDialog
        open={instructorDialogOpen}
        cypId={selectedRecord?.id}
        moduleCode={selectedRecord?.moduleCode}
        moduleName={selectedRecord?.moduleName}
        currentInstructors={selectedRecord?.instructors || []}
        onSave={async (updatedInstructors: InstructorAssignment[]) => {
          if (!selectedRecord) return;
          await TimeCraftInputService.updateModuleInstructors(
            selectedRecord.id,
            updatedInstructors
          );
          // Update local state
          setRecords(prev =>
            prev.map(r =>
              r.id === selectedRecord.id ? { ...r, instructors: updatedInstructors } : r
            )
          );
        }}
        onClose={() => {
          setInstructorDialogOpen(false);
          setSelectedRecord(null);
        }}
      />
    </div>
  );
};

export default CourseYearProperties;
